import { OfferType, QueryParamsData } from '@/types/srp'
import { Container, HorizontalScroll, Typography } from '@cleartrip/ct-design-components'
import Image from 'next/image'
import DefaultPricing from '@/assets/icons/defaultPricing'
import DefaultBank from '@/assets/icons/defaultBank'
import { useState } from 'react'
import CouponDrawer from './CouponDrawer'
import styles from './styles.module.scss'
import { ravenSDKSrpCouponTrigger } from '@/analytics/raven/ravenSDKHelper'
import { useAppSelector } from '@/redux/store/hooks'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useRouter } from 'next/router'
import { processQueryParams } from '@/utils/srp'
import { RAVEN_EVENT_NAME } from '@/analytics/raven/ravenConstants'

type SrpCouponsBarProps = {
    srpCouponList: OfferType[]
    isRevamp?: boolean
}

/** 280x56 card with a 12px radius, per the revamp design. */
const REVAMP_CARD = {
    width: '280px',
    height: '56px',
    padding: '8px 12px',
    boxSizing: 'border-box',
    boxShadow: '0 4px 16px 0 rgba(26, 26, 26, 0.08)'
} as const

/**
 * `overflow-x: auto` on the scroll wrapper forces `overflow-y: auto` too, which clips the
 * cards' shadow on every side — the 16px padding gives the 4px offset + 16px blur room to render.
 */
const REVAMP_SCROLL = { scrollWrapper: { className: `p-4 ${styles.edgeFade}` } }
const REVAMP_BLEED = { margin: '0 -16px' } as const

const SrpCouponsBar: React.FC<SrpCouponsBarProps> = ({ srpCouponList = [], isRevamp = false }) => {
    const [showDrawer, setShowDrawer] = useState<boolean>(false)
    const [clickedCouponCode, setClickedCouponCode] = useState<string>('')
    const { searchData} = useAppSelector(getSrpSearchData)
    const router = useRouter()
    const data = router.query as unknown as QueryParamsData
    const processedData = processQueryParams(router, data)
    const onCouponClick = (index: number) => {
        setShowDrawer(true)
    }
    const handleCouponClick = (index: number,couponData: OfferType) => {
        const ravenPayload = {
            ct_coupon_applied:couponData.MERCH_CODE,
            action_name: "srp_coupons_strip_click",
            action_type: 'click',
            property: 'srp_page',
        }
        setClickedCouponCode(couponData.MERCH_CODE)
        ravenSDKSrpCouponTrigger(RAVEN_EVENT_NAME.A_UI_ACTION, searchData, processedData, { ...ravenPayload })
        onCouponClick(index)
    }
    return (
        <>
            {/*left and right arrow for horizontal scroll*/}
            <Container css={isRevamp ? REVAMP_BLEED : undefined}>
                <HorizontalScroll
                    arrowSize="sm"
                    showArrow={!isRevamp}
                    showShadow={!isRevamp}
                    styleConfig={isRevamp ? REVAMP_SCROLL : undefined}
                >
                    {srpCouponList.map((couponData: OfferType, index: number) => {
                        const { MERCH_DESC, MERCH_SUBDESC, MERCH_ICON, TYPE, MERCH_CODE } = couponData
                        return (
                            <Container
                                key={MERCH_CODE}
                                className={
                                    isRevamp
                                        ? 'flex flex-middle flex-cg-2 flex-no-shrink c-pointer br-12 mr-3 o-hidden ba-solid bc-line-500 bg-neutral-100'
                                        : 'flex flex-middle flex-no-shrink c-pointer px-3 br-12 mr-4 py-2 pl-4 o-hidden'
                                }
                                backgroundColor={isRevamp ? undefined : '#ECF8F4'}
                                css={
                                    isRevamp
                                        ? REVAMP_CARD
                                        : {
                                              width: '310px',
                                              height: '58px'
                                          }
                                }
                                // Single Coupon Click
                                onClick={()=>handleCouponClick(index,couponData)}
                            >
                                {MERCH_ICON ? (
                                    <Image src={MERCH_ICON} alt="icon" height={isRevamp ? 32 : 24} width={isRevamp ? 32 : 24} />
                                ) : TYPE === 'PBO' ? (
                                    <DefaultBank />
                                ) : (
                                    <DefaultPricing />
                                )}
    
                                <Container className={isRevamp ? 'flex flex-column o-hidden' : 'flex flex-column ml-2'}>
                                    <Typography
                                        variant={isRevamp ? 'HM4' : 'B2'}
                                        className="ws-nowrap t-truncate c-pointer"
                                        css={isRevamp ? undefined : { width: '239px' }}
                                    >
                                        {MERCH_DESC}
                                    </Typography>
                                    <Container className="flex flex-middle">
                                        <Typography className="c-pointer" color={isRevamp ? 'success' : undefined} variant="B3CAPS">
                                            {MERCH_CODE}
                                        </Typography>
                                        { MERCH_SUBDESC && <Container className="mx-1 c-pointer">|</Container> }
                                        <Typography
                                            variant={isRevamp ? 'B3' : 'B4'}
                                            className="ws-nowrap t-truncate c-pointer"
                                            color={isRevamp ? 'subheading' : undefined}
                                            css={isRevamp ? undefined : { width: '138px' }}
                                        >
                                            {MERCH_SUBDESC}
                                        </Typography>
                                    </Container>
                                </Container>
                            </Container>
                        )
                    })}
                </HorizontalScroll>
            </Container>
            <CouponDrawer srpCouponList={srpCouponList} showDrawer={showDrawer} setShowDrawer={setShowDrawer} couponClicked={clickedCouponCode} />
        </>
    )
}

export default SrpCouponsBar
